import { EventEmitter, Injectable, Input } from "@angular/core";
import { Observable } from "rxjs";

import { LoginService } from "./login.service";

import { Home } from "../models/home";
import { AppUser } from "../models/app.user";
import { UploadedFile } from "../models/uploaded.file";
import { AppUserMenu } from "../models/app.user.menu";

import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../../environments/environment";

import { NbMenuItem, NbToastrService } from "@nebular/theme";
import { generalSettings } from "../../../main/settings";

import { BehaviorSubject } from "rxjs";
import { Event, Router } from "@angular/router";

import Autolinker from "autolinker";

import { TranslateService } from "@ngx-translate/core";
import { StorageService } from "./storage.service";

@Injectable()
export class GeneralService {

  private URL: string = environment.urlbase;
  private headers: HttpHeaders;
  private storedHome: Home = null;

  @Input() postsPerPage: number = 10;

  public daysSelected$ = new BehaviorSubject(null);
  public home$: BehaviorSubject<Home> = new BehaviorSubject(null);
  public userMenu$: BehaviorSubject<any> = new BehaviorSubject(null);
  public menu!:any;

  constructor(
    private http: HttpClient,
    private toastrService: NbToastrService,
    private loginService: LoginService,
    private translateService: TranslateService,
    private storageService: StorageService,
    private router: Router,
  ) {
    this.translateService.setFallbackLang(generalSettings.defaultLanguage);
    this.headers = this.loginService.getHeaders();
    this.setLanguage();
  }

  private setLanguage(): void {
    // const userLang: string = 'pt';
    const userLang = this.translateService.getBrowserLang();
    const defaultSystemLang: string = generalSettings?.defaultLanguage || "en";
    this.translateService.use(userLang)
    .subscribe({
      next: (): void => {},
      error: (): void => {
        this.translateService.use(defaultSystemLang)
        .subscribe({
          next: (): void => {
            if (!environment.disableDevToolsDetector) {
              console.clear();
            }
          },
        });
      }
    })
  }

  public getBrowserLang(): string {
    let browserLang = this.translateService.getBrowserLang();
    if (browserLang === 'pt') browserLang = 'pt-br';
    return browserLang.match(/pt-br|en/) ? browserLang : 'en'
  }

  public getBrowserCulture(): string {
    return this.translateService.getBrowserCultureLang();
  }

  /* =================================================
     ---------- U S E R   F U N C T I O N S ----------
     =================================================*/

  register(model: AppUser): Observable<AppUser> {
    return this.http.post<AppUser>(this.URL + "user", model);
  }

  updateProfile(model: AppUser | Partial<AppUser>): Observable<AppUser> {
    this.headers = this.loginService.getHeaders();
    return this.http.put<AppUser>(this.URL + "user", model, {
      headers: this.headers,
    });
  }

  acceptTerms(): Observable<AppUser> {
    this.headers = this.loginService.getHeaders();
    return this.http.post<AppUser>(
      this.URL + "user/terms",
      {},
      { headers: this.headers }
    );
  }

  getTranslate(key: string): string {
    let translation: string;
    if (key == "auth.login.registrationButton")
      key += "." + generalSettings.registrationButtonText;
    this.translateService.get(key).subscribe((text: string) => {
      translation = text;
    });
    return translation;
  }

  resend(): Observable<AppUser> {
    this.headers = this.loginService.getHeaders();
    return this.http.post<AppUser>(
      this.URL + "user/resend",
      {},
      { headers: this.headers }
    );
  }

  /* =======================================================
     ---------- G E N E R A L   F U N C T I O N S ----------
     =======================================================*/

  home(): Promise<Home> {
    return new Promise<Home>((next): void => {
      if (this.storedHome !== null && this.storedHome !== undefined) {
        next(this.storedHome);
      } else {
        this.http
        .get<Home>(this.URL + "home", {
          headers: this.loginService.getHeaders(),
        })
        .subscribe((home: Home): void => {
          if (home) {
            this.storedHome = home;
            this.home$.next(home);
          }
          next(home);
        });
      }
    });
  }

  updateHome(model: Home): void {
    this.storedHome = model;
  }

  /**
   * Resolve se a máscara visual de uma área deve ser exibida, com base no
   * `features.mask_options` retornado pelo /home. Regra: se `mask_options`
   * não vier, todas as áreas valem `true`; se a chave da área vier ausente,
   * essa área também vale `true`. `true` = com máscara, `false` = sem máscara.
   */
  maskEnabled(home: Home, section: "home" | "product_detail" | "community" | "ranking"): boolean {
    const opts: NonNullable<Home["features"]>["mask_options"] = home?.features?.mask_options;
    if (!opts) {
      return true;
    }
    return opts[section] ?? true;
  }

  getStoredHome(): Home {
    return this.storedHome;
  }

  userMenu(): Promise<AppUserMenu> {
    return new Promise<AppUserMenu>((next): void => {
      if (this.loginService.isAuthenticated()) {
        if(!this.menu){
          this.loginService.loggedUser$.subscribe(
            (done: AppUser): void => {
              //const storedMenu: AppUserMenu = this.storageService.restoreObject('app_logged_user_menu');
              if (done !== null && done !== undefined && !this.menu) {
                this.http.get<any>(
                  this.URL + 'menu/' + done.id, {
                    headers: this.loginService.getHeaders()
                  }
                ).subscribe(
                  (menu: any): void => {
                    if (menu) this.menu = menu;
                    next(menu);
                  }
                )
              }
            }
          );
        }

      } else next(null);
    });
  }

  getUserMenu() {

  }

  trainings(token: string): Observable<any[]> {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.set("Accept", "application/json");
    if (token !== undefined && token != "" && token !== null) {
      headers = headers.set("Authorization", "Bearer " + token);
    }
    return this.http.get<any[]>(this.URL + "seens", { headers: headers });
  }

  authExtension(): string {
    let ext: string = "";
    if (this.loginService.isAuthenticated()) {
      ext += "/auth";
    }
    return ext;
  }

  upload(file: File, type: string = "image"): Observable<UploadedFile> {
    this.headers = this.loginService.getHeaders();
    let formData: FormData = new FormData();
    formData.append("image", file, file.name);
    return this.http.post<UploadedFile>(this.URL + "file/" + type, formData, {
      headers: this.headers,
    });
  }

  loadGTM(gtmId: string): void {
    const head: HTMLHeadElement = document.head;
    const body: HTMLBodyElement = document.body as HTMLBodyElement;
    const gtmScript: HTMLLinkElement = document.getElementById(
      "gtm-script"
    ) as HTMLLinkElement;
    if (gtmScript === null || gtmScript === undefined) {
      let newGtmScript: HTMLScriptElement = document.createElement("script");
      newGtmScript.id = "gtm-script";
      newGtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');`;
      head.appendChild(newGtmScript);
      let newGtmNoscript: HTMLElement = document.createElement("noscript");
      let newGTMIframe: HTMLIFrameElement = document.createElement("iframe");
      newGTMIframe.src = "https://www.googletagmanager.com/ns.html?id=" + gtmId;
      newGTMIframe.height = "0";
      newGTMIframe.width = "0";
      newGTMIframe.style.display = "none";
      newGTMIframe.style.visibility = "hidden";
      newGtmNoscript.prepend(newGTMIframe);
      body.prepend(newGtmNoscript);
    }
  }

  hasGTM(): boolean {
    const gtmScript: HTMLLinkElement = document.getElementById(
      "gtm-script"
    ) as HTMLLinkElement;
    return gtmScript !== null && gtmScript !== undefined;
  }

  /* =================================================
     ---------- F I L E   F U N C T I O N S ----------
     =================================================*/

  resizeAndCrop(
    image: File,
    resizeToMaxHeightOrWidth: number,
    callback: (resizedAndCroppedImage: File) => void
  ): void {
    let canvas: HTMLCanvasElement = document.createElement("canvas");
    let reader: FileReader = new FileReader();
    reader.readAsDataURL(image);
    reader.onload = (event: ProgressEvent): void => {
      let img: HTMLImageElement = new Image();
      img.src = (<FileReader>event.target).result as string;
      img.onload = (): void => {
        let crop: boolean = false;
        let beginX: number = 0;
        let beginY: number = 0;
        const cropWidth: number = img.width;
        const cropHeight: number = img.height;
        if (img.width > img.height && img.width > resizeToMaxHeightOrWidth) {
          canvas.width = img.height;
          canvas.height = img.height;
          let refHeight: number = img.height;
          if (canvas.width < resizeToMaxHeightOrWidth) {
            canvas.width = resizeToMaxHeightOrWidth;
            refHeight = resizeToMaxHeightOrWidth;
          }
          beginX = (refHeight - img.width) / 2;
          crop = true;
        } else if (
          img.height > img.width &&
          img.height > resizeToMaxHeightOrWidth
        ) {
          canvas.height = img.width;
          canvas.width = img.width;
          let refWidth: number = img.height;
          if (canvas.height < resizeToMaxHeightOrWidth) {
            canvas.height = resizeToMaxHeightOrWidth;
            refWidth = resizeToMaxHeightOrWidth;
          }
          beginY = (refWidth - img.height) / 2;
          crop = true;
        }
        if (crop) {
          const context: CanvasRenderingContext2D = canvas.getContext("2d");
          context.drawImage(img, beginX, beginY, cropWidth, cropHeight);
          img.src = canvas.toDataURL();
          img.onload = (): void => {
            this.resizeImage(
              img,
              canvas,
              resizeToMaxHeightOrWidth,
              (residedFile: File): void => {
                callback(residedFile);
              }
            );
          };
        } else {
          this.resizeImage(
            img,
            canvas,
            resizeToMaxHeightOrWidth,
            (residedFile: File): void => {
              callback(residedFile);
            }
          );
        }
      };
    };
  }

  private resizeImage(
    img: HTMLImageElement,
    canvas: HTMLCanvasElement,
    maxMeasure: number,
    callback: (resizedFile: File) => void
  ): void {
    if (img.width > maxMeasure) {
      canvas.height = maxMeasure;
      canvas.width = maxMeasure;
      const context: CanvasRenderingContext2D = canvas.getContext("2d");
      context.drawImage(img, 0, 0, maxMeasure, maxMeasure);
      img.src = canvas.toDataURL();
      img.onload = (): void => {
        this.imageToFile(img, (file: File): void => {
          callback(file);
        });
      };
    } else {
      this.imageToFile(img, (file: File): void => {
        callback(file);
      });
    }
  }

  private imageToFile(
    img: HTMLImageElement,
    callback: (file: File) => void
  ): void {
    this.getImgContentType(img, (type: string): void => {
      const extension: string =
        type.split("/").length > 1 ? type.split("/")[1] : "jpg";
      fetch(img.src).then((result: Response): void => {
        result.arrayBuffer().then((arraybuffer: ArrayBuffer): void => {
          let file: File = new File(
            [arraybuffer],
            "resized-image." + extension,
            { type: type }
          );
          callback(file);
        });
      });
    });
  }

  private getImgContentType(
    img: HTMLImageElement,
    callback: (mimeType: string) => void
  ): void {
    let xhr: XMLHttpRequest = new XMLHttpRequest();
    xhr.open("HEAD", img.src, true);
    xhr.onreadystatechange = function (
      request: XMLHttpRequest,
      event: Event
    ): any {
      if (request.readyState == request.DONE) {
        callback(xhr.getResponseHeader("Content-Type"));
      }
    }.bind(this, xhr);
    xhr.send();
  }

  /* =====================================================
     ---------- S C R O L L   F U N C T I O N S ----------
     =====================================================*/

  getScrollableElement(): HTMLDivElement {
    const scrollableContainer: HTMLCollection = document.getElementsByClassName('scrollable-container');
    if (this.exists(scrollableContainer) && this.exists(scrollableContainer[0])) {
      return scrollableContainer[0] as HTMLDivElement;
    }
  }

  smoothScrollIntoView(
    scrollDisplacement: number,
    scrollableContainer: HTMLDivElement,
    smooth: boolean = true,
    orientation: "top" | "left",
    callback: () => void
  ): void {
    if (smooth) {
      const steps: number = 30;
      const time: number = (Math.floor((scrollDisplacement / 1000)) + 1) * 300;
      scrollableContainer.scrollTo({[orientation]: 0});
      for (let i: number = 0; i < steps; i++) {
        const displacement: number = (i + 1) * scrollDisplacement / steps;
        const timeout: number = ((i + 1) * time / steps);
        setTimeout((): void => {
          scrollableContainer.scrollTo({[orientation]: displacement});
        }, timeout);
      }
      setTimeout((): void => callback(), time);
    } else {
      setTimeout((): void => {
        scrollableContainer.scrollTo({[orientation]: scrollDisplacement});
        callback();
      }, 300);
    }
  }

  /* =====================================================
     ---------- H E L P E R   F U N C T I O N S ----------
     =====================================================*/

  store(model: any, modelName: string, json: boolean = false): void {
    this.loginService.putCookie(
      modelName,
      json ? JSON.stringify(model) : model
    );
  }

  get(modelName: string, json: boolean = false): any {
    if (
      this.loginService.getCookie(modelName) === null ||
      this.loginService.getCookie(modelName) === undefined ||
      this.loginService.getCookie(modelName) == ""
    ) {
      return false;
    }
    return json
      ? JSON.parse(this.loginService.getCookie(modelName))
      : this.loginService.getCookie(modelName);
  }

  purge(modelName: string): void {
    this.loginService.purgeCookie(modelName);
  }

  exists(element: any): boolean {
    return element !== null && element !== undefined;
  }

  mobileMenu(): NbMenuItem[] {
    let mobileMenu: NbMenuItem[] = [];
    let home: boolean = true;

    const isActivatedCommunityRoute = this.router.url.includes('/comunidade');

    const gs: any = generalSettings;

    if (gs.enableTimeline) {
      mobileMenu.push({
        title: this.getTimelineLabel(),
        icon: { icon: "bt-oportunidades-tab", status: "home" },
        link: "/produtos",
        home: home,
        pathMatch: "full",
      });
      home = false;
    }

    if (this.isExploreEnabled(this.storedHome)) {
      mobileMenu.push({
        title: this.getExploreLabel(),
        icon: { icon: "bt-explorer-tab", status: "explorar" },
        link: "/explorar",
        home: home,
        pathMatch: "full",
      });
      home = false;
    }

    if (gs.enableSociaMedia) {
      mobileMenu.push({
        title: this.getSocialMediaLabel(),
        icon: { icon: "bt-comunidade-tab", status: "comunidade" },
        link: this.loginService.isAuthenticated() ? "/comunidade" : "/login",
        home: home,
        pathMatch: "full",
      });
      home = false;
    }

    // search moved to header
    if (this.loginService.isAuthenticated()) {
      mobileMenu.push({
        title: this.getTranslate("theme.components.header.profile"),
        icon: { icon: "bt-perfil-tab", status: "perfil" },
        link: "/perfil",
        home: home,
        pathMatch: "full",
      });
    }
    return mobileMenu;
  }

  getSocialMediaLabel(): string {
    if (generalSettings.socialMediaLabel !== "Comunidade")
      return generalSettings.socialMediaLabel;
    return this.getTranslate("theme.components.header.socialMediaLabel");
  }

  getExploreLabel(): string {
    if (generalSettings.exploreLabel && generalSettings.exploreLabel !== "Explorar") {
      return generalSettings.exploreLabel;
    }
    return this.getTranslate("theme.components.header.exploreLabel");
  }

  isExploreEnabled(home?:Home): boolean {
    const resolvedHome =
      home || this.storedHome || this.storageService.restoreObject("home_config");

    return !!resolvedHome?.features?.explore_enabled;
  }

  getIaButtonLabel(type: string): string {
    return this.getTranslate(`timeline.lessons.action-ai.${generalSettings.aiButtonsLabel[type]}`);
  }

  getTimelineLabel(): string {
    if (generalSettings.timelineLabel !== "Início")
      return generalSettings.timelineLabel;
    return this.getTranslate("theme.components.header.timelineLabel");
  }

  deviceDetection(device: string): boolean {
    const isAndroid: RegExpMatchArray = navigator.userAgent.match(/Android/i);
    const isBlackBerry: RegExpMatchArray =
      navigator.userAgent.match(/BlackBerry/i);
    const isiOS: RegExpMatchArray =
      navigator.userAgent.match(/iPhone|iPad|iPod/i);
    if (device == "Android") return isAndroid !== null;
    if (device == "BlackBerry") return isBlackBerry !== null;
    if (device == "iOS") return isiOS !== null;
  }

  browserDetectionClass(): void {
    var ua: string = navigator.userAgent.toLowerCase();
    if (ua.indexOf("safari") != -1) {
      if (ua.indexOf("chrome") > -1) {
        document.body.className = document.body.className + " chrome";
      } else {
        document.body.className = document.body.className + " safari";
      }
    }
  }

  fancyHTML(description: string, newLineToBR: boolean = false): string {
    let fancyHTML: string;
    const htmlNewLine: string = newLineToBR ? "<br>" : "";
    if (description == null || description == undefined) fancyHTML = "";
    else {
      fancyHTML = description
        .replace(/&lt;/g, "<")
        .replace(/&LT;/g, "<")
        .replace(/&#x0003C;/g, "<")
        .replace(/&#60;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&GT;/g, ">")
        .replace(/&#x0003E;/g, ">")
        .replace(/&#62;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&QUOT;/g, '"')
        .replace(/&#x00022;/g, '"')
        .replace(/&#34;/g, '"')
        .replace(/(?:\r\n|\r|\n)/g, htmlNewLine);
      var autolinker: Autolinker = new Autolinker();
      fancyHTML = autolinker.link(fancyHTML);
    }
    return fancyHTML;
  }

  public formatLocaleDate(param: any): string {
    const date = new Date(param);
    return date.toLocaleDateString(this.getBrowserLang());
  }

  public showToastService(type: "success" | "warning" | "error", message: string, title: string = '', time: number = 5000): void {
    if (type === "success"){
      this.toastrService.success(
        message,
        title?.length > 1 ? title : this.getTranslate('general.successAlertTitle'),
        {duration: time}
      );
    } else if (type === "warning") {
      this.toastrService.warning(
        message,
        title?.length > 1 ? title : this.getTranslate('general.warningAlertTitle'),
        {duration: time}
      );
    } else if (type === "error") {
      this.toastrService.danger(
        message,
        title?.length > 1 ? title : this.getTranslate('general.errorAlertTitle'),
        {duration: time}
      );
    }
  }
}
